// src/pages/admin/AdminLogin.jsx
// git commit: "fix(AdminLogin): retry on network/5xx errors to survive brief server restart gaps"

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Moon, Sun, Mail, CheckCircle } from "lucide-react";

// Retries once on network errors or 5xx server errors — NOT on 400/401
// (wrong credentials should fail immediately, not retry).
// This specifically covers the brief 1-2s gap when the shared hosting
// process is restarting and a request lands right in that window.
async function postWithRetry(url, data, maxRetries = 3) {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await axios.post(url, data);
      return res;
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      const shouldRetry = !err.response || status >= 500;

      if (!shouldRetry || attempt === maxRetries) {
        throw err;
      }

      await new Promise((r) => setTimeout(r, 1200 * attempt));
      console.log(`[AdminLogin] Retrying attempt ${attempt + 1}...`);
    }
  }
  throw lastError;
}

export default function AdminLogin() {
  const navigate  = useNavigate();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  // "form" → "sent" (after email dispatched)
  const [stage,    setStage]    = useState("form");
  const [errorMsg, setErrorMsg] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    try {
      await postWithRetry("https://api.larrydigitals.com/api/admin/login", { email, password });
      // Credentials accepted — email is on its way
      setStage("sent");
    } catch (err) {
      const status = err.response?.status;

      if (status === 401 || status === 400) {
        setErrorMsg(err.response?.data?.message || "Invalid admin credentials");
      } else if (status === 503) {
        setErrorMsg("Server is still starting up. Please try again in a few seconds.");
      } else if (!err.response) {
        setErrorMsg("Connection error. Please check your internet and try again.");
      } else {
        setErrorMsg(err.response?.data?.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? "bg-gray-950" : "bg-gray-50"}`}>
      {/* Header */}
      <header className="w-full border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-end">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </header>

      <main className="flex items-center justify-center px-4 py-16">
        <div
          className={`w-full max-w-[420px] ${
            darkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
          } border rounded-xl shadow-sm p-8`}
        >
          {stage === "form" ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Admin Login
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Enter your credentials — we'll email you a secure access link
                </p>
              </div>

              {errorMsg && (
                <div className="mb-5 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 text-center">
                  {errorMsg}
                </div>
              )}

              <form className="space-y-5" onSubmit={handleLogin}>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="admin@larrydigitals.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    className={`w-full h-11 px-4 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-500 transition ${
                      darkMode ? "border-gray-700" : "border-gray-300"
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    className={`w-full h-11 px-4 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-500 transition ${
                      darkMode ? "border-gray-700" : "border-gray-300"
                    }`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full h-11 text-sm font-medium rounded-md transition flex items-center justify-center gap-2 ${
                    loading
                      ? "bg-purple-400 cursor-not-allowed"
                      : "bg-purple-600 hover:bg-purple-700"
                  } text-white`}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Send Access Link
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
                Secured access · Authorized administrators only
              </p>
            </>
          ) : (
            /* ── Email sent confirmation ── */
            <div className="text-center">
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Check Your Email
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                A secure access link has been sent to your email address.<br />
                Click the link in the email to sign in to the admin panel.
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 text-xs text-amber-700 dark:text-amber-400 text-left">
                <strong>Important:</strong> The link expires in 20 minutes and can only be used once.
                Check your spam folder if you don't see it.
              </div>
              <button
                onClick={() => setStage("form")}
                className="mt-6 text-sm text-purple-600 dark:text-purple-400 hover:underline"
              >
                ← Back to login
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}